import { Injectable } from '@angular/core';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import {
  IOrderImport,
  IOrderImportDetail,
  IOrderImportResult,
} from '../interfaces/upload-order/order-import';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IOrderImportFilter } from '../interfaces/order-import-filter';
import { ReadService } from './read.service';
import { Observable } from 'rxjs';
import { ServerSentEventService } from './server-sent-event.service';

export enum OrderImportStatus {
  New = 1,
  Validating = 2,
  Valid = 3,
  Invalid = 4,
  ReadyForTransfer = 5,
  ProcessingForTransfer = 6,
  Transferred = 7,
  TransferError = 8,
  Archived = 9,
  Queued = 14,
}

export interface Column {
  field: string;
  header: string;
}

@Injectable({ providedIn: 'root' })
export class OrderImportService extends ReadService<IOrderImport, IOrderImportFilter> {
  constructor(
    private serverSentEventService: ServerSentEventService,
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
  ) {
    super(http, handlersFactory);
  }
  public url = '/api/orderimport';
  public entityTitle = 'OrderImport';
  private sample_spreadsheet_file = '../../../../docs/spreadsheet_import_sample.csv';

  cols: Column[] = [
    { field: 'status', header: 'Status' },
    { field: 'customer_name', header: 'Customer' },
    { field: 'order_type', header: 'Order Type' },
    { field: 'account_code', header: 'Account Code' },
    { field: 'is_vehicle_taxed', header: 'Vehicle Taxed' },
    { field: 'is_mot_valid', header: 'Mot Valid' },
    { field: 'customer_order_number', header: 'Customer Order Number' },
    { field: 'user_email_address', header: 'User Emailadress' },
    { field: 'vehicle_reg', header: 'Reg Plate' },
    { field: 'vin', header: 'VIN' },
    { field: 'make', header: 'Make' },
    { field: 'model', header: 'Model' },
    { field: 'kerb_weight', header: 'Weight' },
    { field: 'pick_up_business_name', header: 'Pick up Business Name' },
    { field: 'pick_up_address_line_1', header: 'Pick up Address Line 1' },
    { field: 'pick_up_address_line_2', header: 'Pick up Address Line 2' },
    { field: 'pick_up_town', header: 'Pick up Town' },
    { field: 'pick_up_postcode', header: 'Pick up Postcode' },
    { field: 'pick_up_earliest', header: 'Pick up Earliest' },
    { field: 'pick_up_latest', header: 'Pick up Latest' },
    { field: 'pick_up_additional_requirements', header: 'Pick up Additional Requirements' },
    { field: 'pick_up_notes', header: 'Pick up Notes' },
    { field: 'drop_off_business_name', header: 'Drop off Business Name' },
    { field: 'drop_off_address_line_1', header: 'Drop off Address Line 1' },
    { field: 'Drop_off_Address_line_2', header: 'Drop off Address Line 2' },
    { field: 'drop_off_address_line_3', header: 'Drop off Address Line 3' },
    { field: 'drop_off_town', header: 'Drop off Town' },
    { field: 'drop_off_postcode', header: 'Drop off Postcode' },
    { field: 'drop_off_earliest', header: 'Drop off Earliest' },
    { field: 'drop_off_latest', header: 'Drop off Latest' },
    { field: 'drop_off_additional_requirements', header: 'Drop off Additional Requirements' },
    { field: 'drop_off_notes', header: 'Drop Off Notes' },
    { field: 'purchase_order_no', header: 'Purchase Order No.' },
    { field: 'job_tagging', header: 'Job Tags' },
    { field: 'department', header: 'Dept.' },
    { field: 'team', header: 'Team' },
    { field: 'enforce_vrm_validation', header: 'Enfroce VRM Validation' },
    { field: 'hold_period', header: 'Hold Period' },
    { field: 'min_price', header: 'Min Price' },
    { field: 'max_price', header: 'Max Price' },
    { field: 'price', header: 'Price' },
    { field: 'order_number', header: 'Engineius Order Number' },
    { field: 'step_group_name', header: 'Step Group Name' },
    { field: 'pick_up_forename', header: 'Pick up Forename' },
    { field: 'pick_up_surname', header: 'Pick up Surname' },
    { field: 'pick_up_email', header: 'Pick up Email' },
    { field: 'pick_up_phone1', header: 'Pick up Phone 1' },
    { field: 'pick_up_phone2', header: 'Pick up Phone 2' },
    { field: 'pick_up_notification', header: 'Pick up Notification' },
    { field: 'drop_off_forename', header: 'Drop Off Forename' },
    { field: 'drop_off_surname', header: 'Drop Off Surname' },
    { field: 'drop_off_email', header: 'Drop Off Email' },
    { field: 'drop_off_phone1', header: 'Drop Off Phone 1' },
    { field: 'drop_off_phone2', header: 'Drop Off Phone 2' },
    { field: 'drop_off_notification', header: 'Drop Off Notification' },
    { field: 'customer_service_fees', header: 'Customer Service Fees' },
    { field: 'fuel_type', header: 'Fuel Type' },
  ];

  public getCompletedOrderImports(filter: IOrderImportFilter): Promise<IOrderImport[]> {
    let params = this.constructHttpParams(filter);
    return this.handleRequest(
      this.http.get(`${this.url}/completed`, { params: params }),
      this.handlersFactory.getForReadByFilter(),
    ).then(this.extractData);
  }

  public getProcessingOrderImports(filter: IOrderImportFilter): Promise<IOrderImport[]> {
    let params = this.constructHttpParams(filter);
    return this.handleRequest(
      this.http.get(`${this.url}/processing`, { params: params }),
      this.handlersFactory.getForReadByFilter(),
    ).then(this.extractData);
  }

  public getOrderImportDetails(id): Promise<IOrderImportDetail[]> {
    return this.handleRequest(
      this.http.get(`${this.url}/order/${id}/order-details`),
      this.handlersFactory.getForReadByFilter(),
    ).then(this.extractData);
  }

  public validate(id: number): Promise<IOrderImportResult> {
    return this.handleRequest(
      this.http.get(`${this.url}/validate/${id}`),
      this.handlersFactory.getForReadByFilter(),
    ).then(this.extractData);
  }

  public removeOrderImport(id: number): Promise<void> {
    return this.handleRequest(
      this.http.delete(`${this.url}/order/${id}`),
      this.handlersFactory.getForReadByFilter(),
    ).then(this.extractData);
  }

  public transferOrderImport(id: number): Promise<void> {
    return this.http.post<void>(`${this.url}/transfer/${id}`, {}).toPromise();
  }

  public removeImportDetail(id: number): Promise<boolean> {
    return this.handleRequest(
      this.http.delete(`${this.url}/order-detail/${id}`),
      this.handlersFactory.getForReadByFilter(),
    ).then(this.extractData);
  }

  public saveImportDetail(id: number, data: IOrderImportDetail): Promise<any> {
    let successMsg = data.id ? 'Order added.' : 'Order updated.';
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });
    return this.handleRequest(
      this.http.post(`${this.url}/order-detail`, data, { headers: headers }),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
  }
}
